/**
 * 解析 `Goal.diagnosis_content` JSON → step2 展示字段。
 * 兼容 present_risk SSE 同构与 issues/plan 结构。
 */

import type { DiagnosisUnlockRiskItem } from "./types";

const RISK_LEVELS: DiagnosisUnlockRiskItem["riskLevel"][] = [
  "high",
  "medium",
  "low",
];

const isRiskLevel = (
  value: unknown,
): value is DiagnosisUnlockRiskItem["riskLevel"] =>
  typeof value === "string" &&
  RISK_LEVELS.includes(value as DiagnosisUnlockRiskItem["riskLevel"]);

const mapSeverityToRiskLevel = (
  value: unknown,
): DiagnosisUnlockRiskItem["riskLevel"] => {
  if (isRiskLevel(value)) {
    return value;
  }
  if (typeof value !== "string") {
    return "medium";
  }
  const normalized = value.toLowerCase();
  if (normalized.includes("critical") || normalized.includes("high")) {
    return "high";
  }
  if (normalized.includes("minor") || normalized.includes("low")) {
    return "low";
  }
  return "medium";
};

export const parseRiskItemRecord = (
  item: unknown,
): DiagnosisUnlockRiskItem | null => {
  if (!item || typeof item !== "object") {
    return null;
  }

  const record = item as Record<string, unknown>;
  const title =
    (typeof record.title === "string" && record.title.trim()) ||
    (typeof record.name === "string" && record.name.trim()) ||
    "";
  const description =
    (typeof record.description === "string" && record.description.trim()) ||
    (typeof record.detail === "string" && record.detail.trim()) ||
    "";

  if (!title || !description) {
    return null;
  }

  return {
    title,
    description,
    riskLevel: mapSeverityToRiskLevel(record.riskLevel ?? record.severity),
  };
};

export type ParsedDiagnosisContentFields = {
  riskSummary: string;
  riskItems: DiagnosisUnlockRiskItem[];
  planSummary?: string;
  embeddedTrackerSpecs?: { title: string; description?: string }[];
};

export function parseDiagnosisContentFields(
  raw: string | undefined,
): ParsedDiagnosisContentFields | null {
  if (!raw?.trim()) {
    return null;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  if (!parsed || typeof parsed !== "object") {
    return null;
  }

  const record = parsed as Record<string, unknown>;

  const riskSummary =
    (typeof record.riskSummary === "string" && record.riskSummary.trim()) ||
    (typeof record.report_summary_for_card === "string" &&
      record.report_summary_for_card.trim()) ||
    (typeof record.report_summary === "string" &&
      record.report_summary.trim()) ||
    (typeof record.title === "string" && record.title.trim()) ||
    (typeof record.headline === "string" && record.headline.trim()) ||
    (typeof record.summary === "string" && record.summary.trim()) ||
    "";

  const riskItemsSource = Array.isArray(record.riskItems)
    ? record.riskItems
    : Array.isArray(record.issues)
      ? record.issues
      : Array.isArray(record.findings)
        ? record.findings
        : [];

  const riskItems = riskItemsSource
    .map(parseRiskItemRecord)
    .filter((item): item is DiagnosisUnlockRiskItem => item != null);

  const plan =
    record.plan && typeof record.plan === "object"
      ? (record.plan as Record<string, unknown>)
      : null;
  const planSummary =
    (typeof plan?.summary === "string" && plan.summary.trim()) ||
    (typeof record.summary === "string" && record.summary.trim()) ||
    undefined;

  const goalDraft =
    record.goalDraft && typeof record.goalDraft === "object"
      ? (record.goalDraft as Record<string, unknown>)
      : null;
  const embeddedTrackerSpecs = Array.isArray(goalDraft?.trackerSpecs)
    ? goalDraft.trackerSpecs
        .map((spec) => {
          if (!spec || typeof spec !== "object") {
            return null;
          }
          const title = (spec as Record<string, unknown>).title;
          if (typeof title !== "string" || !title.trim()) {
            return null;
          }
          const description = (spec as Record<string, unknown>).description;
          return {
            title: title.trim(),
            ...(typeof description === "string" && description.trim()
              ? { description: description.trim() }
              : {}),
          };
        })
        .filter(
          (spec): spec is { title: string; description?: string } =>
            spec != null,
        )
    : undefined;

  if (!riskSummary || riskItems.length === 0) {
    return null;
  }

  return {
    riskSummary,
    riskItems,
    planSummary,
    embeddedTrackerSpecs:
      embeddedTrackerSpecs && embeddedTrackerSpecs.length > 0
        ? embeddedTrackerSpecs
        : undefined,
  };
}
